import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { API } from '@/config/api';
import { env } from '@/config/env';
import { onSessionEnded } from '@/shared/auth/session';
import { deviceId } from '@/shared/auth/deviceId';
import { refreshAccessToken } from '@/shared/auth/refresh';
import { tokenStorage } from '@/shared/auth/tokenStorage';

import { ApiError } from './ApiError';
import { isOAuthError, isProblemDetail } from './problem';

/** Marks a request that has already been retried, so a loop cannot form. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  retriedAfterRefresh?: boolean;
}

/**
 * The single HTTP client. Nothing in this application calls `axios` or `fetch`
 * directly.
 *
 * <h2>Why one client</h2>
 *
 * Authentication, refresh, error translation and timeouts are decisions that have
 * to hold for every request. Made per call site they hold for most requests, and
 * the one that forgot is found by a user.
 *
 * <h2>Requests are relative in development</h2>
 *
 * `baseURL` is empty and the dev server proxies `/api` and `/oauth2`, so the
 * browser never leaves its origin — no CORS to configure, and the same code path
 * in development and behind a reverse proxy.
 */
/**
 * What the sign-in screen says when the session ended by itself.
 *
 * <p>Shared by both routes into {@link onSessionEnded} so the person reads the same
 * sentence whether the refresh token was missing or refused -- two mechanisms, one
 * fact: they are signed out and need to sign in again.
 */
const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
  /*
   * Repeated keys for an array, not indexed ones.
   *
   * Axios writes `product=['a','b']` as `product[]=a&product[]=b`, and Spring reads that
   * as a parameter literally NAMED `product[]` -- so a `List<String> product` binds to
   * nothing and the filter silently does not apply. `indexes: null` writes
   * `product=a&product=b`, which is what a repeatable query parameter is.
   *
   * Set on the instance rather than per call, because the failure is invisible: no error,
   * no warning, just a filter that does not filter. One call site remembering this is one
   * call site; the next one will not.
   */
  paramsSerializer: { indexes: null },
});

/**
 * Attaches the bearer token.
 *
 * <p>Temporary, and the trade is stated in {@link tokenStorage}: the backend
 * supports HttpOnly cookies, and moving to them means deleting this interceptor
 * and setting `withCredentials`. Everything else stays as it is.
 */
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Never on the token endpoint. Asking for a session is not an authenticated
  // request, and a stale token sent alongside the credentials is read as a
  // CLIENT credential -- Spring finds an Authorization header it cannot parse and
  // answers 401, rejecting the application rather than judging the password. The
  // failure then looks like bad credentials and is not.
  if (config.url === API.token) {
    // The one header this endpoint does take. It says which browser is asking, so
    // the backend can replace this user's previous session HERE and leave their
    // phone and their laptop signed in -- see deviceId for what the value is and
    // what it is not.
    //
    // Here rather than at the call sites, because there are three of them and a
    // fourth coming: the password grant, Google, GitHub, and whatever provider is
    // wired next. A device id missing from one of them is not an error anywhere --
    // the sign-in succeeds and quietly leaves a session behind, which is the
    // failure this whole mechanism exists to stop. Omitted when storage cannot
    // hold one, for the reason that module gives.
    const device = deviceId();
    if (device !== null) {
      config.headers.set('X-Device-ID', device);
    }
    return config;
  }

  const token = tokenStorage.accessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * Recovers from an expired token, then turns every remaining failure into an
 * {@link ApiError}.
 *
 * <h2>Why a 401 is not simply an error</h2>
 *
 * Access tokens last fifteen minutes. Without this, a tab left open over lunch
 * comes back to every request failing and no explanation — the refresh token sits
 * in storage, unused, valid for thirty days. So a 401 is first an instruction to
 * refresh and try again, and only an error if that fails.
 *
 * <h2>The four things that must not be retried</h2>
 *
 * The token endpoint itself, because a failure there is bad credentials rather
 * than an expired session. A request already retried once, or a loop forms.
 * And a 401 with no refresh token, because there is nothing to try.
 *
 * <p>**And logout**, added 4 October 2026, which is the one that cost something.
 * Refreshing a session in order to revoke it is incoherent, and a 401 from logout
 * already means the thing logout wanted: the session is gone. What made it worse
 * than pointless is where the failed refresh ends up — `onSessionEnded` does
 * `window.location.assign('/login')`, a FULL page load, so a 401 on logout threw
 * away whatever address the browser was on.
 *
 * <p>That address is sometimes an activation link. "Sign out and continue" on
 * `/activate/set-password?token=…` calls `revokeSession()`, and a 401 there
 * navigated to `/login` and took the single-use token with it — the person is
 * signed out, as they asked, and the errand they were signing out FOR is gone
 * from the address bar with nothing able to recover it. `activation.md` AC-50 is
 * explicit that the link survives signing out; this was the hole in it.
 */
http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    const axiosError = error as AxiosError;
    const config = axiosError.config as RetriableConfig | undefined;

    // No response: offline, timed out, or nothing listening. There is no status
    // and no body to read, and a caller that forgets this case shows "undefined".
    if (!axiosError.response) {
      throw ApiError.fromNetwork();
    }

    const { status } = axiosError.response;

    // A 401 this application can still do something about: not a replay, not the
    // token endpoint, and not logout.
    const recoverable401 =
      status === 401 &&
      config !== undefined &&
      !config.retriedAfterRefresh &&
      config.url !== API.token &&
      // Logout, for the reason in the comment above: a 401 here is logout's own
      // outcome, and refreshing to retry it ends in a hard navigation that
      // discards the address -- including an activation link's token.
      config.url !== API.logout;

    // No refresh token, so there is nothing to exchange and the session is over.
    //
    // This used to fall through to the generic handling below, which turned a dead
    // session into whatever sentence the server happened to send -- "Full
    // authentication is required to access this resource" -- rendered inside a
    // screen that still showed the person's name in the header. They were signed
    // out in fact and signed in in appearance, with no way to tell which.
    //
    // It ends the session for the same reason a failed refresh does: the remedy is
    // signing in again and there is no second place to look. The refresh token is
    // also held in an HttpOnly cookie, but nothing reads that back today -- the
    // refresh grant takes the token from a request parameter, and no filter
    // supplies it from the cookie -- so an empty store really does mean empty.
    if (recoverable401 && !tokenStorage.refreshToken()) {
      onSessionEnded(SESSION_ENDED_MESSAGE);
      throw ApiError.fromSessionExpired();
    }

    if (recoverable401) {
      let accessToken: string;
      try {
        accessToken = await refreshAccessToken();
      } catch {
        // Only a failed REFRESH ends the session. The token is gone, expired or
        // rejected as reused, there is nothing further to try, and saying so once
        // here beats every queued request surfacing its own 401 to somebody who
        // is simply signed out.
        onSessionEnded(SESSION_ENDED_MESSAGE);
        throw ApiError.fromSessionExpired();
      }

      // Deliberately outside that try. A replay that still fails is about this
      // request -- a resource the caller may not reach -- and folding it into the
      // catch above would sign somebody out over one forbidden endpoint, having
      // just proved their session is fine by renewing it.
      config.retriedAfterRefresh = true;
      config.headers.set('Authorization', `Bearer ${accessToken}`);
      return await http.request(config);
    }

    const data = await readFailureBody(axiosError.response.data);
    if (isProblemDetail(data)) {
      throw ApiError.fromProblem(data);
    }
    if (isOAuthError(data)) {
      throw ApiError.fromOAuth(data, status);
    }

    // A 5xx that is not a problem document did not come from this application --
    // every failure it raises answers application/problem+json. It came from
    // whatever sits in front: a proxy, a load balancer, a gateway that could not
    // reach us. In development that is Vite's dev proxy answering 500 text/plain
    // when nothing is listening on 8080.
    //
    // Worth its own answer, because the generic one blames the application for a
    // fault it never saw and tells somebody to try again immediately against a
    // server that is down.
    if (status >= 500) {
      throw ApiError.fromUnreachable(status);
    }

    throw ApiError.fromUnexpectedResponse(status);
  },
);

/**
 * The failure's body, parsed.
 *
 * <p>A streamed request (`apiClient.stream`) asks for the body unread, so a refusal arrives as a
 * stream rather than parsed JSON. Read and parsed here, the refusal's problem document reaches
 * the caller the same as any other request's; anything unreadable is treated as no body.
 */
async function readFailureBody(body: unknown): Promise<unknown> {
  if (typeof ReadableStream === 'undefined' || !(body instanceof ReadableStream)) return body;
  try {
    const text = await new Response(body).text();
    return text === '' ? null : (JSON.parse(text) as unknown);
  } catch {
    return null;
  }
}
